import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { GalleriaModule } from 'primeng/galleria'
import { Subscription } from 'rxjs'
import { FileModel } from 'src/app/models/file.model'
import { FileTypePipe } from 'src/app/pipes/file-type.pipe'
import { FileProcessingService } from 'src/app/services/file-processing.service'
import { GalleriaService, GalleriaState } from 'src/app/services/galleria.service'
import { config } from 'src/environments/environment'

@Component({
  selector: 'app-root-galleria',
  templateUrl: './root-galleria.component.html',
  styleUrls: ['./root-galleria.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, GalleriaModule, FileTypePipe, FontAwesomeModule],
})
export class RootGalleriaComponent implements OnInit, OnDestroy {
  mediaUrl = config.mediaUrl
  files: FileModel[] = []
  activeIndex = 0
  visible = false

  private sub: Subscription

  // fileName -> corrected (unwrapped) data URI, populated only when an image fails to render
  // because the underlying blob was stored double-base64-encoded (legacy offline uploads).
  // The unwrapped full image is reused for both the item and thumbnail views.
  private recovered = new Map<string, string>()
  private attempted = new Set<string>()

  constructor(
    private galleriaService: GalleriaService,
    private fileProcessing: FileProcessingService
  ) {}

  imageSrc(file: FileModel): string {
    return this.recovered.get(file.fileName) ?? this.mediaUrl + file.fileName
  }

  thumbnailSrc(file: FileModel): string {
    return this.recovered.get(file.fileName) ?? this.mediaUrl + 'thumbnail/' + file.fileName
  }

  // When an image fails to render, try to recover it. Double-encoded blobs load with a 200 but
  // cannot be decoded; fetching the full image and unwrapping the extra base64 layer yields a
  // valid data URI. Attempted once per file to avoid an error/reload loop.
  async onImageError(file: FileModel): Promise<void> {
    const key = file.fileName
    if (this.attempted.has(key)) return
    this.attempted.add(key)

    try {
      const blob = await this.fileProcessing.readUrlAsBlob(this.mediaUrl + file.fileName)
      const dataUri = (await this.fileProcessing.convertBlobToBase64(blob)) as string
      const normalised = this.fileProcessing.normaliseDoubleEncodedDataUri(dataUri)
      // Only swap when the image was actually double-encoded (unwrap changed the value).
      if (normalised !== dataUri) {
        this.recovered.set(key, normalised)
      }
    } catch {
      // leave the broken image as-is
    }
  }

  ngOnInit(): void {
    this.sub = this.galleriaService.state$.subscribe((state: GalleriaState) => {
      this.files = state.files
      this.activeIndex = state.activeIndex
      this.visible = state.visible
      console.log(this.files)
    })
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe()
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.galleriaService.close()
    }
  }

  onActiveIndexChange(index: number): void {
    this.galleriaService.setActiveIndex(index)
  }
}
